/**
 * Product Catalog Module Documentation (Detailed)
 */

export const productCatalogDoc = {
  title: 'Product Catalog (Detailed)',
  content: `# Product Catalog Module - Detailed Technical Guide

## 1. Introduction

The Engraveca Product Catalog uses a **template-based system** designed for businesses that sell customizable products. This architecture separates:

- **Templates**: Define HOW a product type works (pricing rules, available options, specifications)
- **Products**: Define WHAT you sell (specific items customers see and purchase)

| Traditional Catalog | Template-Based Catalog |
|---------------------|------------------------|
| Each product has its own pricing | Pricing rules shared across products |
| Hard to maintain consistency | Change template = update all products |
| Difficult to add new options | Add option to template = available everywhere |

---

## 2. System Architecture

\`\`\`mermaid
flowchart LR
    subgraph Admin["Admin Configuration"]
        OS[Option Sets] --> T[Templates]
    end
    
    subgraph Catalog["Product Catalog"]
        T --> P[Products]
    end
    
    subgraph Customer["Customer Experience"]
        P --> SF[Storefront]
        SF --> Cart[Shopping Cart]
        Cart --> Order[Order]
    end
    
    style OS fill:#e8d5c4
    style T fill:#d4c4b0
    style P fill:#c0b09c
    style SF fill:#f5f5f0
    style Cart fill:#f5f5f0
    style Order fill:#8B7355,color:#fff
\`\`\`

### Key Components

| Component | Purpose | Location |
|-----------|---------|----------|
| **Option Sets** | Reusable customization choices | Catalog → Option Sets |
| **Templates** | Product type blueprints | Catalog → Templates |
| **Products** | Customer-facing listings | Catalog → Products |

**Data Flow:** Option Sets → Templates → Products → Storefront

---

## 3. The Template-Product Relationship

\`\`\`mermaid
flowchart TB
    subgraph Template["Template: Vinyl Banners"]
        PM[Pricing Model: Per Sq Ft]
        MU[Markup: 300%]
        OPT[Options: Material, Finish]
    end
    
    Template -->|inherits| P1[13oz Banner]
    Template -->|inherits| P2[15oz Banner]
    Template -->|inherits| P3[Mesh Banner]
    
    style Template fill:#8B7355,color:#fff
    style P1 fill:#d4c4b0
    style P2 fill:#d4c4b0
    style P3 fill:#d4c4b0
\`\`\`

### What is a Template?

A template is a **blueprint** that defines:

1. **Pricing Model**: How the price is calculated
2. **Markup Rules**: Profit margin settings
3. **Available Options**: Which customizations apply
4. **Specifications**: Default product specifications

**Think of it like this:**
- **Template** = "Business Card" (the category/type)
- **Product** = "Premium Matte Business Cards" (specific listing)

### Inheritance

When you create a product from a template, the product **inherits**:
- Pricing Model, Markup %, Option Sets, File Requirements

The **Product** customizes:
- Name, Images, Description, Status, Pricing Override (optional)

**Best Practice**: 90%+ of products should NOT override template pricing.

---

## 4. Product Types Explained

\`\`\`mermaid
flowchart TD
    Start[What are you selling?] --> Q1{Custom Size?}
    
    Q1 -->|Yes| LGF[Large Format]
    Q1 -->|No| Q2{Fixed Quantities?}
    
    Q2 -->|Yes| SMF[Small Format]
    Q2 -->|No| AWD[Awards]
    
    LGF --> PSF[Per Square Foot]
    SMF --> QT[Quantity Tiers]
    AWD --> BPO[Base + Options]
    
    style Start fill:#8B7355,color:#fff
    style LGF fill:#3b82f6,color:#fff
    style SMF fill:#22c55e,color:#fff
    style AWD fill:#f59e0b,color:#fff
\`\`\`

### Large Format (LGF)
**Examples**: Banners, Signs, Posters, Vehicle Wraps

| Setting | Value |
|---------|-------|
| Pricing Model | Per Square Foot |
| Size | Custom dimensions |
| Key Options | Material, Finish, Mounting |

### Small Format (SMF)
**Examples**: Business Cards, Flyers, Brochures

| Setting | Value |
|---------|-------|
| Pricing Model | Quantity Tiers |
| Size | Preset sizes |
| Key Options | Paper, Finish, Sides |

### Awards (AWD)
**Examples**: Trophies, Plaques, Crystal Awards

| Setting | Value |
|---------|-------|
| Pricing Model | Base + Options |
| Size | Preset sizes with prices |
| Key Options | Size, Personalization, Logo |

---

## 5. Pricing Models Deep Dive

### Per Square Foot Calculation Flow

\`\`\`mermaid
flowchart LR
    subgraph Input["Customer Input"]
        W[Width: 48in]
        H[Height: 96in]
    end
    
    subgraph Calc["Calculation"]
        SQFT["÷ 144 = 32 sqft"]
        PRICE["× $2.50 = $80"]
    end
    
    subgraph Final["Result"]
        OUT["$80.00"]
    end
    
    W --> SQFT
    H --> SQFT
    SQFT --> PRICE
    PRICE --> OUT
    
    style OUT fill:#22c55e,color:#fff
\`\`\`

### Per Square Foot Model (Large Format)

| Field | Description | Example |
|-------|-------------|---------|
| base_price_per_sqft | Retail per sq ft | $2.50 |
| cost_per_sqft | Your cost per sq ft | $0.625 |
| min_sqft | Minimum billable | 4 sq ft |
| min_price | Minimum order | $25.00 |
| default_markup_percent | Markup | 300% |

**Example**: 4ft × 8ft Banner = 32 sq ft × $2.50 = **$80.00**

### Quantity Tiers Model (Small Format)

| Quantity | Your Cost | Retail Price |
|----------|-----------|--------------|
| 100 | $5.00 | $17.50 |
| 250 | $10.00 | $30.00 |
| 500 | $15.00 | $42.50 |
| 1000 | $25.00 | $67.50 |

*Retail = Cost × (1 + 150%) + $5.00 fixed*

### Base + Options Model (Awards)

| Size | Dimensions | Cost | Retail |
|------|------------|------|--------|
| Small | 6 inches | $15 | $85 |
| Medium | 8 inches | $22 | $113 |
| Large | 10 inches | $35 | $165 |

**Add-ons**: Extra lines ($5/line), Logo ($25)

---

## 6. Option Sets & Customization

### What is an Option Set?

A reusable group of choices attached to templates:

| Option Set | Choices |
|------------|---------|
| Finish | Matte, Gloss, UV Coated |
| Material | 13oz Vinyl, 15oz Vinyl, Mesh |
| Mounting | Grommets, Pole Pocket, Stand |

### Price Modifiers

| Type | Description | Example |
|------|-------------|---------|
| Percent | Add % to base | Gloss: +10% |
| Fixed | Add flat amount | Lamination: +$15 |
| Per Sq Ft | Add per sq ft | Premium: +$0.50/sqft |

---

## 7. Complete Workflow Examples

### Creating a Banner Product Line

**Step 1: Create Option Sets**
- Banner Material: 13oz Vinyl, 15oz Vinyl, Mesh

**Step 2: Create Template**
- Name: Vinyl Banners, Code: BAN
- Type: LGF, Model: Per Sq Ft
- Markup: 300%, Base: $2.50/sqft
- Option Sets: [Banner Material, Finish]

**Step 3: Create Products**
- "Standard Vinyl Banner" → Template: Vinyl Banners

### Creating Business Cards

**Step 1: Create Template**
- Name: Business Cards, Code: BC
- Type: SMF, Model: Quantity Tiers
- Markup: 150%, Fixed: $5.00
- Tiers: 100/$5, 250/$10, 500/$15, 1000/$25

---

## 8. Troubleshooting Guide

| Issue | Cause | Solution |
|-------|-------|----------|
| Product not showing | Status is Draft | Set to Published |
| Wrong price | Pricing override | Remove override |
| Options missing | Not linked | Add option set to template |
| Can't create product | No templates | Create active template |

### Markup Quick Reference

| Markup % | Multiplier | $10 → Retail |
|----------|------------|--------------|
| 100% | 2× | $20 |
| 150% | 2.5× | $25 |
| 200% | 3× | $30 |
| 300% | 4× | $40 |

---

*Version 2.0 | December 2025*
`
};
